import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../data/config';
import { DINO_ORDER, FRAME_H, FRAME_W, registerDinoAnimations } from '../data/dinos';

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  preload() {
    this.drawLoadingBar();

    // Dino sprite sheets — same frame size for all four.
    for (const id of DINO_ORDER) {
      this.load.spritesheet(`dino-${id}`, `assets/dinos/${id}.png`, {
        frameWidth: FRAME_W,
        frameHeight: FRAME_H,
      });
    }

    // World assets.
    this.load.image('stripes', 'assets/world/stripes.png');
    this.load.image('shadow', 'assets/world/shadow.png');

    // Comms portraits — Mika (mothership pilot, Star Fox 64 style), Bosco
    // (the team's pet whippet), and MR CONSEQUENCE (villain mastermind who
    // taunts before bosses + seethes after losing them) chime in at key moments.
    this.load.image('comms-mika', 'assets/comms/mika.png');
    this.load.image('comms-bosco', 'assets/comms/bosco.png');
    this.load.image('comms-consequence', 'assets/comms/consequence.png');


    // Generate procedural placeholders for things we don't have art for yet.
    this.generatePlaceholderTextures();
  }

  create() {
    for (const id of DINO_ORDER) {
      registerDinoAnimations(this, id);
    }
    this.scene.start('Title');
  }

  // --- helpers -----------------------------------------------------------

  private drawLoadingBar() {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    const label = this.add
      .text(cx, cy - 18, 'LOADING', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#f4ecff',
      })
      .setOrigin(0.5);

    const barW = 160;
    const barH = 6;
    const frame = this.add
      .rectangle(cx, cy, barW, barH)
      .setStrokeStyle(1, 0xf4ecff)
      .setOrigin(0.5);
    const fill = this.add
      .rectangle(cx - barW / 2 + 1, cy, 1, barH - 2, 0xf4ecff)
      .setOrigin(0, 0.5);

    this.load.on('progress', (p: number) => {
      fill.width = (barW - 2) * p;
    });
    this.load.on('complete', () => {
      frame.destroy();
      fill.destroy();
      label.destroy();
    });
  }

  // ---------- Procedural planet sprites for the level-select map ----------

  private circle(g: Phaser.GameObjects.Graphics, color: number, alpha: number, x: number, y: number, r: number) {
    g.fillStyle(color, alpha);
    g.fillCircle(x, y, r);
  }

  private makePlanetIce(key: string) {
    const g = this.add.graphics();
    // Soft outer halo
    this.circle(g, 0x80c8ff, 0.18, 32, 32, 31);
    // Body
    this.circle(g, 0x4080c0, 1, 32, 32, 28);
    // Darker mid swirl
    this.circle(g, 0x2860a0, 1, 28, 36, 14);
    // Highlight crescent
    this.circle(g, 0xc8e4ff, 1, 24, 22, 8);
    // Polar caps
    this.circle(g, 0xffffff, 1, 32, 8, 5);
    this.circle(g, 0xffffff, 1, 32, 56, 4);
    // Snowflakes
    g.fillStyle(0xffffff, 1);
    g.fillRect(20, 30, 1, 1);
    g.fillRect(40, 38, 1, 1);
    g.fillRect(46, 26, 1, 1);
    g.fillRect(30, 44, 1, 1);
    g.generateTexture(key, 64, 64);
    g.destroy();
  }

  private makePlanetLava(key: string) {
    const g = this.add.graphics();
    this.circle(g, 0xff6020, 0.25, 32, 32, 31);
    this.circle(g, 0x3a0808, 1, 32, 32, 28);
    this.circle(g, 0x802020, 1, 28, 30, 18);
    // Lava cracks running across the surface
    g.fillStyle(0xff5020, 1);
    g.fillRect(12, 24, 18, 2);
    g.fillRect(34, 36, 16, 2);
    g.fillRect(20, 44, 14, 2);
    g.fillRect(40, 18, 8, 2);
    // Hot spot glow
    g.fillStyle(0xffa020, 1);
    g.fillCircle(34, 32, 5);
    g.fillStyle(0xffe060, 1);
    g.fillCircle(34, 32, 2);
    // Smoke wisps
    this.circle(g, 0x101010, 0.5, 18, 14, 3);
    this.circle(g, 0x101010, 0.4, 48, 50, 3);
    g.generateTexture(key, 64, 64);
    g.destroy();
  }

  private makePlanetDunes(key: string) {
    const g = this.add.graphics();
    this.circle(g, 0xffc080, 0.18, 32, 32, 31);
    this.circle(g, 0xc08040, 1, 32, 32, 28);
    // Sand bands — slight ellipses for atmospheric striping
    g.fillStyle(0x9a5a28, 1);
    g.fillRect(8, 22, 48, 2);
    g.fillRect(10, 36, 44, 2);
    g.fillRect(14, 48, 36, 2);
    // Highlight
    this.circle(g, 0xffe0a0, 1, 24, 24, 8);
    // Tiny dunes detail
    g.fillStyle(0x603020, 1);
    g.fillRect(40, 28, 3, 1);
    g.fillRect(20, 42, 3, 1);
    g.fillRect(46, 44, 3, 1);
    g.generateTexture(key, 64, 64);
    g.destroy();
  }

  private makePlanetCanopy(key: string) {
    const g = this.add.graphics();
    this.circle(g, 0x80ff80, 0.18, 32, 32, 31);
    this.circle(g, 0x408040, 1, 32, 32, 28);
    // Forest patches
    g.fillStyle(0x205020, 1);
    g.fillCircle(22, 26, 7);
    g.fillCircle(40, 36, 6);
    g.fillCircle(28, 44, 5);
    g.fillCircle(48, 22, 4);
    // Bright highlight
    this.circle(g, 0xa8ffa8, 1, 24, 22, 5);
    // Sparkle
    g.fillStyle(0xffffff, 1);
    g.fillRect(24, 22, 1, 1);
    g.generateTexture(key, 64, 64);
    g.destroy();
  }

  /**
   * Generate a tinted 16×16 ground tile keyed under the given texture name.
   * Three colors: base, top edge highlight, accent flecks.
   */
  private makeGroundTile(key: string, base: number, top: number, fleck: number) {
    const g = this.add.graphics();
    g.fillStyle(base, 1).fillRect(0, 0, 16, 16);
    g.fillStyle(top, 1).fillRect(0, 0, 16, 3);
    g.fillStyle(fleck, 1);
    g.fillRect(2, 6, 1, 1);
    g.fillRect(9, 9, 1, 1);
    g.fillRect(13, 4, 1, 1);
    g.fillRect(5, 12, 1, 1);
    g.generateTexture(key, 16, 16);
    g.destroy();
  }

  /**
   * We need a few simple shapes (enemy body, particle, ground tile, kick fx).
   * Building them with the Graphics API at boot saves shipping art for v1.
   */
  private generatePlaceholderTextures() {
    // 1px white pixel — used for particle/sparkle systems.
    const pixel = this.add.graphics();
    pixel.fillStyle(0xffffff, 1).fillRect(0, 0, 1, 1);
    pixel.generateTexture('px', 1, 1);
    pixel.destroy();

    // Default ground tile (used as fallback / for ledges across all planets).
    const ground = this.add.graphics();
    ground.fillStyle(0x1a1330, 1).fillRect(0, 0, 16, 16);
    ground.fillStyle(0x2c1f4a, 1).fillRect(0, 0, 16, 3);
    ground.fillStyle(0x0a0612, 1);
    ground.fillRect(2, 6, 2, 2);
    ground.fillRect(9, 9, 2, 2);
    ground.fillRect(13, 4, 2, 2);
    ground.generateTexture('ground', 16, 16);
    ground.destroy();

    // Per-planet ground tiles (16×16). Each gets its own palette + accent
    // pixels so the world reads as visually distinct as soon as you land.
    this.makeGroundTile('ground-doux', 0x2a3a55, 0x4a6a90, 0xc8e4ff); // Glacier — icy blue
    this.makeGroundTile('ground-mort', 0x4a1414, 0x80261a, 0xff6438); // Inferno — charred red
    this.makeGroundTile('ground-tard', 0x4a3014, 0x80552a, 0xffc060); // Dunes — sand
    this.makeGroundTile('ground-vita', 0x143820, 0x2a6028, 0x90ff60); // Canopy — jungle green

    // Lava tile (animated-looking via dithered hot/cold patches). Placed in
    // pits on Mort's planet — touching it = instant death.
    const lava = this.add.graphics();
    lava.fillStyle(0xff3a1a, 1).fillRect(0, 0, 16, 16);
    lava.fillStyle(0xffa800, 1);
    lava.fillRect(0, 0, 16, 3);
    lava.fillRect(2, 5, 3, 1);
    lava.fillRect(9, 7, 3, 1);
    lava.fillRect(5, 11, 3, 1);
    lava.fillStyle(0xffe23a, 1);
    lava.fillRect(3, 1, 2, 1);
    lava.fillRect(10, 1, 2, 1);
    lava.generateTexture('lava', 16, 16);
    lava.destroy();

    // Icicle — pointy-down 6×12 hazard for Glacier. Light blue with a white
    // highlight for that "frozen needle" feel.
    const icicle = this.add.graphics();
    icicle.fillStyle(0x9ad8ff, 1);
    icicle.fillTriangle(0, 0, 6, 0, 3, 12);
    icicle.fillStyle(0xffffff, 1);
    icicle.fillRect(2, 1, 1, 6);
    icicle.generateTexture('icicle', 6, 12);
    icicle.destroy();

    // Bounce mushroom — 16×8 spongy green pad for Canopy.
    const mush = this.add.graphics();
    mush.fillStyle(0x7a3aa0, 1);
    mush.fillRect(2, 4, 12, 4); // stem
    mush.fillStyle(0x60ff60, 1);
    mush.fillRect(0, 0, 16, 5); // cap
    mush.fillStyle(0xc8ffd0, 1);
    mush.fillRect(2, 1, 2, 1); // highlights
    mush.fillRect(8, 1, 2, 1);
    mush.fillRect(12, 1, 2, 1);
    mush.fillRect(4, 3, 2, 1);
    mush.fillRect(10, 3, 2, 1);
    mush.generateTexture('bounce', 16, 8);
    mush.destroy();

    // Star fragment pickup: 5x5 yellow diamond.
    const star = this.add.graphics();
    star.fillStyle(0xffe23a, 1);
    star.fillRect(2, 0, 1, 1);
    star.fillRect(1, 1, 3, 1);
    star.fillRect(0, 2, 5, 1);
    star.fillRect(1, 3, 3, 1);
    star.fillRect(2, 4, 1, 1);
    star.generateTexture('star', 5, 5);
    star.destroy();

    // Big star — 11x11 with bright white core + outer glow ring. Worth more
    // ammo and score, placed at high/hard-to-reach spots.
    const bigStar = this.add.graphics();
    bigStar.fillStyle(0xffe23a, 1);
    bigStar.fillRect(5, 0, 1, 1);
    bigStar.fillRect(3, 1, 5, 1);
    bigStar.fillRect(2, 2, 7, 1);
    bigStar.fillRect(1, 3, 9, 1);
    bigStar.fillRect(0, 4, 11, 1);
    bigStar.fillRect(0, 5, 11, 1);
    bigStar.fillRect(0, 6, 11, 1);
    bigStar.fillRect(1, 7, 9, 1);
    bigStar.fillRect(2, 8, 7, 1);
    bigStar.fillRect(3, 9, 5, 1);
    bigStar.fillRect(5, 10, 1, 1);
    bigStar.fillStyle(0xffffff, 1);
    bigStar.fillRect(4, 4, 3, 3);
    bigStar.fillRect(5, 3, 1, 1);
    bigStar.fillRect(5, 7, 1, 1);
    bigStar.fillRect(3, 5, 1, 1);
    bigStar.fillRect(7, 5, 1, 1);
    bigStar.generateTexture('big-star', 11, 11);
    bigStar.destroy();

    // Heart pickup: tiny pixel heart 7x6.
    const heart = this.add.graphics();
    heart.fillStyle(0xff3a6b, 1);
    heart.fillRect(1, 1, 2, 1);
    heart.fillRect(4, 1, 2, 1);
    heart.fillRect(0, 2, 7, 2);
    heart.fillRect(1, 4, 5, 1);
    heart.fillRect(2, 5, 3, 1);
    heart.generateTexture('heart', 7, 6);
    heart.destroy();

    // Default alien blob (used as fallback) — kept for backwards compat.
    const alien = this.add.graphics();
    alien.fillStyle(0x8c2bff, 1);
    alien.fillRect(2, 4, 12, 8);
    alien.fillRect(3, 3, 10, 1);
    alien.fillRect(1, 5, 1, 6);
    alien.fillRect(14, 5, 1, 6);
    alien.fillRect(3, 12, 10, 1);
    alien.fillStyle(0xffffff, 1);
    alien.fillRect(5, 6, 2, 2);
    alien.fillRect(10, 6, 2, 2);
    alien.fillStyle(0x000000, 1);
    alien.fillRect(6, 7, 1, 1);
    alien.fillRect(11, 7, 1, 1);
    alien.generateTexture('alien', 16, 14);
    alien.destroy();

    // ICE BLOB (Glacier) — jagged crystal silhouette with shimmering shards
    // and a single dark eye. Reads "frozen creature."
    const iceBlob = this.add.graphics();
    iceBlob.fillStyle(0x9ad8ff, 1);
    iceBlob.fillRect(3, 5, 10, 7); // body
    iceBlob.fillRect(4, 4, 8, 1);
    iceBlob.fillRect(5, 3, 6, 1); // top peak
    iceBlob.fillRect(2, 6, 1, 5);
    iceBlob.fillRect(13, 6, 1, 5);
    iceBlob.fillRect(4, 12, 8, 1);
    // ice shards on top
    iceBlob.fillStyle(0xffffff, 1);
    iceBlob.fillRect(4, 2, 1, 2);
    iceBlob.fillRect(8, 1, 1, 3);
    iceBlob.fillRect(11, 2, 1, 2);
    iceBlob.fillRect(2, 9, 1, 1); // sparkles
    iceBlob.fillRect(13, 7, 1, 1);
    // eye
    iceBlob.fillStyle(0x0a3050, 1);
    iceBlob.fillRect(7, 7, 2, 2);
    iceBlob.fillStyle(0xffffff, 1);
    iceBlob.fillRect(7, 7, 1, 1);
    iceBlob.generateTexture('alien-doux', 16, 14);
    iceBlob.destroy();

    // EMBER (Inferno) — squat fireball with two flame tongues licking up,
    // glowing core, no visible eyes (they're glowing pits).
    const ember = this.add.graphics();
    ember.fillStyle(0xff3a1a, 1);
    ember.fillRect(2, 6, 12, 6); // body
    ember.fillRect(3, 5, 10, 1);
    ember.fillRect(4, 4, 3, 1); // left flame tongue
    ember.fillRect(9, 4, 3, 1); // right flame tongue
    ember.fillRect(5, 3, 1, 1);
    ember.fillRect(10, 3, 1, 1);
    ember.fillRect(3, 12, 10, 1);
    // hot core
    ember.fillStyle(0xffa800, 1);
    ember.fillRect(4, 7, 8, 4);
    ember.fillStyle(0xffe23a, 1);
    ember.fillRect(5, 8, 6, 2);
    // glowing eye-pits (dark)
    ember.fillStyle(0x4a0a00, 1);
    ember.fillRect(6, 9, 1, 1);
    ember.fillRect(9, 9, 1, 1);
    ember.generateTexture('alien-mort', 16, 14);
    ember.destroy();

    // SAND CRAB (Dunes) — wider stance, eye stalks, sand-tan body with
    // two tiny pincer claws on the sides.
    const crab = this.add.graphics();
    crab.fillStyle(0xc8a060, 1);
    crab.fillRect(3, 6, 10, 7); // body
    crab.fillRect(4, 5, 8, 1);
    crab.fillRect(2, 7, 1, 5);
    crab.fillRect(13, 7, 1, 5);
    crab.fillRect(4, 13, 8, 1);
    // pincer claws (sticking out sides)
    crab.fillStyle(0x8a5a30, 1);
    crab.fillRect(0, 9, 2, 2);
    crab.fillRect(14, 9, 2, 2);
    crab.fillRect(1, 8, 1, 1);
    crab.fillRect(14, 8, 1, 1);
    // eye stalks (poke up from the body)
    crab.fillStyle(0xc8a060, 1);
    crab.fillRect(5, 3, 1, 3);
    crab.fillRect(10, 3, 1, 3);
    // eyes on stalks
    crab.fillStyle(0xffffff, 1);
    crab.fillRect(4, 2, 2, 2);
    crab.fillRect(10, 2, 2, 2);
    crab.fillStyle(0x000000, 1);
    crab.fillRect(5, 3, 1, 1);
    crab.fillRect(11, 3, 1, 1);
    crab.generateTexture('alien-tard', 16, 14);
    crab.destroy();

    // VINE FROG (Canopy) — squat round body, two horn/leaf tufts on top,
    // wide eyes, deep green.
    const frog = this.add.graphics();
    frog.fillStyle(0x4ab040, 1);
    frog.fillRect(2, 5, 12, 8); // round body
    frog.fillRect(3, 4, 10, 1);
    frog.fillRect(1, 6, 1, 6);
    frog.fillRect(14, 6, 1, 6);
    frog.fillRect(3, 13, 10, 1);
    // leaf tufts on top
    frog.fillStyle(0x80ff60, 1);
    frog.fillRect(3, 2, 2, 3);
    frog.fillRect(11, 2, 2, 3);
    frog.fillRect(4, 1, 1, 1);
    frog.fillRect(11, 1, 1, 1);
    // big amphibian eyes
    frog.fillStyle(0xffffff, 1);
    frog.fillRect(4, 7, 3, 3);
    frog.fillRect(9, 7, 3, 3);
    frog.fillStyle(0x103820, 1);
    frog.fillRect(5, 8, 2, 2);
    frog.fillRect(10, 8, 2, 2);
    frog.fillStyle(0xffffff, 1);
    frog.fillRect(5, 8, 1, 1);
    frog.fillRect(10, 8, 1, 1);
    // mouth
    frog.fillStyle(0x103820, 1);
    frog.fillRect(6, 12, 4, 1);
    frog.generateTexture('alien-vita', 16, 14);
    frog.destroy();

    // Spike trap: 12x6 triangular spike row.
    const spike = this.add.graphics();
    spike.fillStyle(0xc0c8d4, 1);
    for (let i = 0; i < 12; i += 4) {
      spike.fillTriangle(i, 6, i + 2, 0, i + 4, 6);
    }
    spike.generateTexture('spike', 12, 6);
    spike.destroy();

    // Muzzle flash: 10x10 starburst (white-yellow).
    const fx = this.add.graphics();
    fx.fillStyle(0xffffff, 1);
    fx.fillRect(4, 0, 2, 10);
    fx.fillRect(0, 4, 10, 2);
    fx.fillStyle(0xffe23a, 1);
    fx.fillRect(2, 2, 2, 2);
    fx.fillRect(6, 2, 2, 2);
    fx.fillRect(2, 6, 2, 2);
    fx.fillRect(6, 6, 2, 2);
    fx.generateTexture('muzzle-fx', 10, 10);
    fx.destroy();

    // Bullet: a flying star with a comet tail. The star at the front (right
    // side, since textures flip horizontally for left-firing) literally
    // matches the pickup star sprite — same shape, same yellow.
    //
    // Layout (14×7, head on the right):
    //   .........  ..*..      (star top point)
    //   ........  .***.
    //   yyyyy....*****
    //   ::::::yy.*****       (gradient trail behind)
    //   yyyyy....*****
    //   ........  .***.
    //   .........  ..*..
    const bullet = this.add.graphics();
    // Trail (left side, 0..7) — light then bright yellow gradient
    bullet.fillStyle(0xffe23a, 0.55);
    bullet.fillRect(0, 3, 5, 1);
    bullet.fillStyle(0xffe23a, 0.85);
    bullet.fillRect(5, 3, 4, 1);
    bullet.fillStyle(0xffe23a, 1);
    bullet.fillRect(7, 2, 2, 3);
    // Star head (cols 9..13)
    bullet.fillStyle(0xffe23a, 1);
    bullet.fillRect(11, 0, 1, 1);
    bullet.fillRect(10, 1, 3, 1);
    bullet.fillRect(9, 2, 5, 3);
    bullet.fillRect(10, 5, 3, 1);
    bullet.fillRect(11, 6, 1, 1);
    // White hot core
    bullet.fillStyle(0xffffff, 1);
    bullet.fillRect(11, 3, 1, 1);
    bullet.generateTexture('bullet', 14, 7);
    bullet.destroy();

    // Generic asteroid boss kept as a fallback texture.
    const boss = this.add.graphics();
    boss.fillStyle(0x6b6480, 1).fillCircle(28, 28, 26);
    boss.fillStyle(0x504a66, 1).fillCircle(28, 28, 22);
    boss.fillStyle(0x5e567a, 1).fillCircle(28, 28, 18);
    boss.fillStyle(0x3c3650, 1);
    boss.fillCircle(18, 22, 4);
    boss.fillCircle(36, 32, 5);
    boss.fillStyle(0xff3a4a, 1).fillRect(20, 25, 4, 3).fillRect(32, 25, 4, 3);
    boss.generateTexture('boss-asteroid', 56, 56);
    boss.destroy();

    // BOSS DOUX — FROZEN COMET (56×56). Pale-blue body with sharp ice
    // spikes radiating outward, white shimmer, single dark glaring eye.
    const bd = this.add.graphics();
    bd.fillStyle(0x6abfff, 1).fillCircle(28, 28, 22);
    bd.fillStyle(0x9ad8ff, 1).fillCircle(28, 28, 18);
    bd.fillStyle(0xc8e4ff, 1).fillCircle(24, 24, 5);
    // Radial ice spikes
    bd.fillStyle(0xffffff, 1);
    bd.fillTriangle(28, 0, 23, 10, 33, 10); // top
    bd.fillTriangle(28, 56, 23, 46, 33, 46); // bottom
    bd.fillTriangle(0, 28, 10, 23, 10, 33); // left
    bd.fillTriangle(56, 28, 46, 23, 46, 33); // right
    bd.fillTriangle(8, 8, 16, 12, 12, 16);
    bd.fillTriangle(48, 8, 40, 12, 44, 16);
    bd.fillTriangle(8, 48, 16, 44, 12, 40);
    bd.fillTriangle(48, 48, 40, 44, 44, 40);
    // Glaring eye
    bd.fillStyle(0x0a1a3a, 1).fillCircle(28, 28, 6);
    bd.fillStyle(0xff3a4a, 1).fillRect(26, 26, 4, 3);
    bd.fillStyle(0xffffff, 1).fillRect(26, 26, 1, 1);
    bd.generateTexture('boss-doux', 56, 56);
    bd.destroy();

    // BOSS MORT — MAGMA ORB (56×56). Charred crust with bright lava cracks
    // and a glowing yellow core. Ominous, pulsing.
    const bm = this.add.graphics();
    bm.fillStyle(0x2a0408, 1).fillCircle(28, 28, 26);
    bm.fillStyle(0x6a1810, 1).fillCircle(28, 28, 22);
    bm.fillStyle(0xa0301a, 1).fillCircle(28, 28, 17);
    bm.fillStyle(0xff4a1a, 1).fillCircle(28, 28, 12);
    bm.fillStyle(0xffa800, 1).fillCircle(28, 28, 8);
    bm.fillStyle(0xffe23a, 1).fillCircle(28, 28, 4);
    // Lava cracks running across the crust
    bm.fillStyle(0xff6a1a, 1);
    bm.fillRect(28, 4, 2, 6);
    bm.fillRect(4, 28, 6, 2);
    bm.fillRect(46, 28, 6, 2);
    bm.fillRect(28, 46, 2, 6);
    bm.fillRect(40, 14, 2, 4);
    bm.fillRect(12, 38, 4, 2);
    bm.fillRect(38, 40, 4, 2);
    bm.fillStyle(0xffe23a, 1);
    bm.fillRect(28, 6, 1, 3);
    bm.fillRect(48, 28, 3, 1);
    bm.generateTexture('boss-mort', 56, 56);
    bm.destroy();

    // BOSS TARD — SAND WORM (56×56). Oblong armored body with segmentation,
    // circular toothed maw in the center, two red side eyes.
    const bt = this.add.graphics();
    // Body
    bt.fillStyle(0x705028, 1).fillRect(4, 16, 48, 28);
    bt.fillStyle(0x8a6a38, 1).fillRect(8, 12, 40, 4);
    bt.fillStyle(0x8a6a38, 1).fillRect(8, 44, 40, 4);
    bt.fillRect(2, 20, 2, 20);
    bt.fillRect(52, 20, 2, 20);
    // Segment lines
    bt.fillStyle(0x4a3018, 1);
    bt.fillRect(16, 12, 2, 36);
    bt.fillRect(40, 12, 2, 36);
    // Open circular mouth
    bt.fillStyle(0x1a0808, 1).fillCircle(28, 30, 9);
    // Inward-pointing teeth/mandibles
    bt.fillStyle(0xe8d090, 1);
    bt.fillTriangle(20, 26, 24, 30, 20, 34);
    bt.fillTriangle(36, 26, 32, 30, 36, 34);
    bt.fillTriangle(28, 22, 24, 26, 32, 26);
    bt.fillTriangle(28, 38, 24, 34, 32, 34);
    // Side eyes
    bt.fillStyle(0xff3a4a, 1);
    bt.fillRect(8, 22, 5, 3);
    bt.fillRect(43, 22, 5, 3);
    bt.fillStyle(0xffffff, 1);
    bt.fillRect(9, 22, 1, 1);
    bt.fillRect(44, 22, 1, 1);
    bt.generateTexture('boss-tard', 56, 56);
    bt.destroy();

    // BOSS VITA — SKY HUNTER (56×56). Pterodactyl silhouette: angular body,
    // wide wings, sharp downward beak, single piercing eye.
    const bv = this.add.graphics();
    // Wings — wide spread
    bv.fillStyle(0x2a6028, 1);
    bv.fillRect(0, 22, 20, 6);
    bv.fillRect(0, 28, 14, 4);
    bv.fillRect(36, 22, 20, 6);
    bv.fillRect(42, 28, 14, 4);
    bv.fillStyle(0x4ab040, 1);
    bv.fillRect(2, 23, 16, 3);
    bv.fillRect(38, 23, 16, 3);
    // Wing tips that point upward
    bv.fillTriangle(0, 22, 0, 14, 6, 22);
    bv.fillTriangle(56, 22, 56, 14, 50, 22);
    // Body — torso
    bv.fillStyle(0x4ab040, 1);
    bv.fillRect(20, 18, 16, 18);
    bv.fillRect(22, 36, 12, 4);
    bv.fillRect(24, 40, 8, 4);
    // Head
    bv.fillRect(22, 12, 12, 8);
    // Crest
    bv.fillStyle(0x80ff60, 1);
    bv.fillTriangle(28, 4, 24, 12, 32, 12);
    // Sharp beak pointing down
    bv.fillStyle(0xffe23a, 1);
    bv.fillTriangle(24, 20, 32, 20, 28, 30);
    // Eye
    bv.fillStyle(0xff3a4a, 1);
    bv.fillRect(25, 14, 6, 3);
    bv.fillStyle(0xffffff, 1);
    bv.fillRect(26, 14, 2, 1);
    // Tail with diamond tip
    bv.fillStyle(0x4ab040, 1);
    bv.fillRect(26, 44, 4, 4);
    bv.fillStyle(0x80ff60, 1);
    bv.fillTriangle(28, 50, 24, 46, 32, 46);
    bv.generateTexture('boss-vita', 56, 56);
    bv.destroy();

    // UFO: 18x10 saucer with cyan dome.
    const ufo = this.add.graphics();
    ufo.fillStyle(0x444466, 1);
    ufo.fillRect(0, 5, 18, 3); // body
    ufo.fillRect(2, 8, 14, 1); // underside lip
    ufo.fillStyle(0x9ad8ff, 1);
    ufo.fillRect(6, 2, 6, 3); // dome
    ufo.fillRect(7, 1, 4, 1);
    ufo.fillStyle(0xffffff, 1);
    ufo.fillRect(8, 2, 1, 1); // dome highlight
    ufo.fillStyle(0xffe23a, 1);
    ufo.fillRect(3, 6, 1, 1); // running lights
    ufo.fillRect(8, 6, 1, 1);
    ufo.fillRect(13, 6, 1, 1);
    ufo.generateTexture('ufo', 18, 10);
    ufo.destroy();

    // Power-up icons — 14×14 colored disc with a single letter centered on
    // top. Shape stays consistent across types so they read as "the same
    // class of pickup," color/letter signal which one.
    const drawPowerUp = (key: string, color: number, letter: string) => {
      const g = this.add.graphics();
      g.fillStyle(0x0d0a1a, 1).fillCircle(7, 7, 7);
      g.fillStyle(color, 1).fillCircle(7, 7, 6);
      g.fillStyle(0xffffff, 1).fillCircle(7, 7, 4);
      g.fillStyle(color, 1).fillCircle(7, 7, 3);
      g.generateTexture(key, 14, 14);
      g.destroy();
      // Overlay the letter as a separate sprite — Phaser Graphics doesn't
      // do text. We bake a per-key letter texture by drawing pixel letters.
      const letterMap: Record<string, [number, number][]> = {
        R: [[0,0],[1,0],[2,0],[0,1],[2,1],[0,2],[1,2],[0,3],[2,3],[0,4],[2,4]],
        S: [[1,0],[2,0],[0,1],[1,2],[2,3],[0,4],[1,4]],
        M: [[0,0],[2,0],[0,1],[1,1],[2,1],[0,2],[2,2],[0,3],[2,3],[0,4],[2,4]],
        T: [[0,0],[1,0],[2,0],[1,1],[1,2],[1,3],[1,4]],
      };
      const lg = this.add.graphics();
      lg.fillStyle(0x0d0a1a, 1);
      const pts = letterMap[letter] || [];
      pts.forEach(([x, y]) => lg.fillRect(x, y, 1, 1));
      lg.generateTexture(`power-letter-${letter}`, 3, 5);
      lg.destroy();
    };
    drawPowerUp('power-rapid', 0xff8c1a, 'R');
    drawPowerUp('power-shield', 0x4ac8ff, 'S');
    drawPowerUp('power-magnet', 0xc864ff, 'M');
    drawPowerUp('power-triple', 0x6cd97a, 'T');

    // Per-planet circular textures for the level-select map. Each is 64×64
    // with a distinct biome paint job: ice caps on Glacier, lava cracks on
    // Inferno, sand bands on Dunes, forest patches on Canopy.
    this.makePlanetIce('planet-doux');
    this.makePlanetLava('planet-mort');
    this.makePlanetDunes('planet-tard');
    this.makePlanetCanopy('planet-vita');

    // Touch button bg: 48x48 translucent disc.
    const btn = this.add.graphics();
    btn.fillStyle(0xffffff, 0.18);
    btn.fillCircle(24, 24, 24);
    btn.lineStyle(2, 0xffffff, 0.5);
    btn.strokeCircle(24, 24, 23);
    btn.generateTexture('btn-bg', 48, 48);
    btn.destroy();

    // Touch joystick base: 64x64 ring.
    const stickBase = this.add.graphics();
    stickBase.lineStyle(2, 0xffffff, 0.4);
    stickBase.strokeCircle(32, 32, 30);
    stickBase.fillStyle(0xffffff, 0.08);
    stickBase.fillCircle(32, 32, 30);
    stickBase.generateTexture('stick-base', 64, 64);
    stickBase.destroy();

    // Touch joystick knob: 28x28 disc.
    const stickKnob = this.add.graphics();
    stickKnob.fillStyle(0xffffff, 0.7);
    stickKnob.fillCircle(14, 14, 14);
    stickKnob.generateTexture('stick-knob', 28, 28);
    stickKnob.destroy();
  }
}
